/**
 * The state behind where the replicas chart looks: the dragged zoom
 * (TRA-1437) and the arrows that move the stretch shown to earlier or
 * later times. Both hold absolute times, so a usage refresh or a scaling
 * refetch replaces the rows and the chart still shows the same stretch.
 * The math is in replicaPan; this only keeps the state.
 */

import { useState } from 'react'

import {
  AT_NOW,
  shownRange,
  stepped,
  viewAt,
  type PanDirection,
  type PanState,
} from '@/components/models/home/replicaPan'
import type { TimeRange } from '@/components/models/home/replicaZoom'

export interface ReplicaView {
  /** The stretch to draw, or null for the whole loaded window: at now, not zoomed. */
  shown: TimeRange | null
  /** The hours of samples the reads must cover so the stretch is loaded. */
  loadHours: number
  zoomed: boolean
  /** The stretch no longer ends at now. */
  moved: boolean
  canEarlier: boolean
  canLater: boolean
  zoom: (range: TimeRange) => void
  /** Clears the zoom and keeps the end time: the full window ending there. */
  resetZoom: () => void
  earlier: () => void
  later: () => void
  backToNow: () => void
}

/**
 * The window starts out at now with no zoom. The caller keys the chart by
 * its window, so a new window mounts a fresh view. The metrics page builds
 * its own ReplicaView from the page's state (useMetricsZoom).
 */
export function useReplicaView(windowHours: number): ReplicaView {
  const [state, setState] = useState<PanState>(AT_NOW)
  // The flags come from one clock read (viewAt). Read apart, a later read
  // sits past the span's end and both arrows of a 7-day window at now
  // come out enabled, with neither moving anything.
  const at = viewAt(state, windowHours)

  function step(direction: PanDirection): void {
    setState((s) => stepped(s, windowHours, direction, Date.now()))
  }

  return {
    shown: shownRange(state, windowHours),
    loadHours: at.loadHours,
    zoomed: state.zoom !== null,
    moved: state.end !== null,
    canEarlier: at.canEarlier,
    canLater: at.canLater,
    zoom: (range) => { setState((s) => ({ end: s.end, zoom: range })) },
    resetZoom: () => { setState((s) => ({ end: s.end, zoom: null })) },
    earlier: () => { step('earlier') },
    later: () => { step('later') },
    backToNow: () => { setState(AT_NOW) },
  }
}
